// src/modulos/Cadastros/ClientListReportPage.js

import React, { useState, useMemo, useEffect } from 'react';
import { auth, db } from '../../firebaseConfig';
import {
    collection,
    getDocs,
    query,
    orderBy,
    addDoc,
    deleteDoc,
    doc,
    serverTimestamp
} from 'firebase/firestore';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import useFirestore from '../../hooks/useFirestore';
import useSubCollection from '../../hooks/useSubCollection';
import {
    FormModal,
    ConfirmModal,
    PageHeader,
    Pagination,
    Toast
} from '../../components/common';
import styles from '../Registers/Registers.module.css';
import { countryOptions } from '../../data/countries';

// --- Collections ---
const CLIENTS_COLLECTION = 'clients';
const REPORT_HISTORY_COLLECTION = 'client-report-history';
const BILLING_CONDITIONS_COLLECTION = 'parameters/data/billing-conditions';
const REGIONS_COLLECTION = 'parameters/data/regioes';
const ROLES_COLLECTION = 'parameters/data/cargos';

const PAYMENT_DOC_PATH = {
    parentCollection: 'parameters',
    parentId: 'types',
    subCollectionName: 'documento-pagamento',
};

const TIPO_CLIENTE_OPTIONS = [
    { value: 'PADRAO', label: 'PADRAO' },
    { value: 'ESPECIAL', label: 'ESPECIAL' },
    { value: 'VIP', label: 'VIP' },
];

const SITUACAO_OPTIONS = [
    { value: 'Ativo', label: 'Ativo' },
    { value: 'Inativo', label: 'Inativo' },
];

const ITEMS_PER_PAGE = 10;

const ClientListReportPage = () => {
    // Report state
    const [reports, setReports] = useState([]);
    const [loadingReports, setLoadingReports] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [reportToDelete, setReportToDelete] = useState(null);
    const [toasts, setToasts] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);

    // Filter states
    const [filters, setFilters] = useState({
        searchTerm: '',
        tipo: '',
        situacao: '',
        regiaoId: '',
        formaPagamentoId: '',
        condicaoFaturamentoId: '',
        pais: '',
        hasCreditoAprovado: false,
        minDesconto: '',
        maxDesconto: '',
        dateFrom: '',
        dateTo: ''
    });

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');

    // Fetch clients
    const { data: clients, loading: clientsLoading } = useFirestore(CLIENTS_COLLECTION, 'codigo');

    // Fetch parameters for filters (only when modal is open)
    const [shouldLoadParams, setShouldLoadParams] = useState(false);

    const { data: billingConditions, loading: billingLoading } = useFirestore(
        shouldLoadParams ? BILLING_CONDITIONS_COLLECTION : null,
        'name'
    );
    const { data: regions, loading: regionsLoading } = useFirestore(
        shouldLoadParams ? REGIONS_COLLECTION : null,
        'name'
    );
    const { data: paymentDocs, loading: paymentLoading } = useSubCollection(
        shouldLoadParams ? PAYMENT_DOC_PATH.parentCollection : null,
        shouldLoadParams ? PAYMENT_DOC_PATH.parentId : null,
        shouldLoadParams ? PAYMENT_DOC_PATH.subCollectionName : null,
        'nome'
    );

    const paramsLoading = billingLoading || regionsLoading || paymentLoading;

    // Get current user
    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            setCurrentUser(user);
        });
        return () => unsubscribe();
    }, []);

    // Load report history
    useEffect(() => {
        loadReports();
    }, []);

    // Load parameters when modal opens
    useEffect(() => {
        if (isModalOpen && !shouldLoadParams) {
            setShouldLoadParams(true);
        }
    }, [isModalOpen, shouldLoadParams]);

    const addToast = (message, type) => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => {
            setToasts(prev => prev.filter(toast => toast.id !== id));
        }, 5000);
    };

    const loadReports = async () => {
        setLoadingReports(true);
        try {
            const q = query(
                collection(db, REPORT_HISTORY_COLLECTION),
                orderBy('generatedAt', 'desc')
            );
            const snapshot = await getDocs(q);
            const reportsList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                generatedAt: doc.data().generatedAt?.toDate() || new Date()
            }));
            setReports(reportsList);
        } catch (error) {
            console.error("Error loading report history:", error);
            addToast('Erro ao carregar histórico', 'error');
        } finally {
            setLoadingReports(false);
        }
    };

    const handleDeleteClick = (reportId) => {
        setReportToDelete(reportId);
        setIsConfirmOpen(true);
    };

    const deleteReport = async () => {
        if (!reportToDelete) return;

        try {
            await deleteDoc(doc(db, REPORT_HISTORY_COLLECTION, reportToDelete));
            setReports(reports.filter(r => r.id !== reportToDelete));
            addToast('Relatório excluído com sucesso', 'success');
        } catch (error) {
            console.error("Error deleting report:", error);
            addToast('Erro ao excluir relatório', 'error');
        } finally {
            setIsConfirmOpen(false);
            setReportToDelete(null);
        }
    };

    // Apply filters to clients
    const filteredClients = useMemo(() => {
        if (!clients) return [];

        let filtered = [...clients];

        // Search term (código, razão social, nome fantasia, CNPJ/CPF)
        if (filters.searchTerm) {
            const term = filters.searchTerm.toLowerCase();
            filtered = filtered.filter(client =>
                (client.codigo || '').toLowerCase().includes(term) ||
                (client.razaoSocial || '').toLowerCase().includes(term) ||
                (client.nomeFantasia || '').toLowerCase().includes(term) ||
                (client.cnpjCpf || '').toLowerCase().includes(term)
            );
        }

        // Filter by tipo
        if (filters.tipo) {
            filtered = filtered.filter(client => client.tipo === filters.tipo);
        }

        // Filter by situacao
        if (filters.situacao) {
            filtered = filtered.filter(client => client.situacao === filters.situacao);
        }

        // Filter by região
        if (filters.regiaoId) {
            filtered = filtered.filter(client => client.regiaoId === filters.regiaoId);
        }

        // Filter by forma de pagamento
        if (filters.formaPagamentoId) {
            filtered = filtered.filter(client => client.formaPagamentoId === filters.formaPagamentoId);
        }

        // Filter by condição de faturamento
        if (filters.condicaoFaturamentoId) {
            filtered = filtered.filter(client => client.condicaoFaturamentoId === filters.condicaoFaturamentoId);
        }

        // Filter by país
        if (filters.pais) {
            filtered = filtered.filter(client => client.pais === filters.pais);
        }

        // Filter by crédito aprovado
        if (filters.hasCreditoAprovado) {
            filtered = filtered.filter(client => client.creditoAprovado === true);
        }

        // Filter by discount range
        if (filters.minDesconto) {
            const min = parseFloat(filters.minDesconto);
            if (!isNaN(min)) {
                filtered = filtered.filter(client => {
                    const desconto = parseFloat(client.percentualDesconto) || 0;
                    return desconto >= min;
                });
            }
        }

        if (filters.maxDesconto) {
            const max = parseFloat(filters.maxDesconto);
            if (!isNaN(max)) {
                filtered = filtered.filter(client => {
                    const desconto = parseFloat(client.percentualDesconto) || 0;
                    return desconto <= max;
                });
            }
        }

        // Filter by creation date range
        if (filters.dateFrom) {
            const fromDate = new Date(filters.dateFrom);
            fromDate.setHours(0, 0, 0, 0);
            filtered = filtered.filter(client => {
                const createdAt = client.createdAt ? new Date(client.createdAt) : null;
                return createdAt && createdAt >= fromDate;
            });
        }

        if (filters.dateTo) {
            const toDate = new Date(filters.dateTo);
            toDate.setHours(23, 59, 59, 999);
            filtered = filtered.filter(client => {
                const createdAt = client.createdAt ? new Date(client.createdAt) : null;
                return createdAt && createdAt <= toDate;
            });
        }

        // Sort by código
        filtered.sort((a, b) => (a.codigo || '').localeCompare(b.codigo || ''));

        return filtered;
    }, [clients, filters]);

    // Pagination for main list
    const totalPages = Math.ceil((filteredClients?.length || 0) / ITEMS_PER_PAGE);
    const currentClients = (filteredClients || []).slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

    // Helper functions
    const getRegiaoName = (regiaoId) => {
        return regions?.find(r => r.id === regiaoId)?.name || regiaoId || '-';
    };

    const getPaymentDocName = (paymentId) => {
        return paymentDocs?.find(p => p.id === paymentId)?.nome || paymentId || '-';
    };

    const getBillingConditionName = (billingId) => {
        return billingConditions?.find(b => b.id === billingId)?.name || billingId || '-';
    };

    const getTipoLabel = (tipo) => {
        return TIPO_CLIENTE_OPTIONS.find(t => t.value === tipo)?.label || tipo || '-';
    };

    const getCountryLabel = (pais) => {
        return countryOptions.find(c => c.value === pais)?.label || pais || '-';
    };

    const generateExcelReport = async (items, filtersObj, reportTitle, fileName) => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Clientes');

        // Title row
        worksheet.mergeCells('A1:N1');
        const titleCell = worksheet.getCell('A1');
        titleCell.value = reportTitle;
        titleCell.font = { size: 16, bold: true };
        titleCell.alignment = { horizontal: 'center' };

        // Generation info
        worksheet.mergeCells('A2:N2');
        worksheet.getCell('A2').value = `Gerado em: ${new Date().toLocaleString('pt-BR')}`;
        worksheet.getCell('A2').font = { size: 10, italic: true };

        worksheet.mergeCells('A3:N3');
        worksheet.getCell('A3').value = `Gerado por: ${currentUser?.email || 'Usuário'}`;
        worksheet.getCell('A3').font = { size: 10, italic: true };

        // Filter info
        let filterRow = 4;
        const filterParts = [];
        if (filtersObj.tipoLabel) filterParts.push(`Tipo: ${filtersObj.tipoLabel}`);
        if (filtersObj.situacao) filterParts.push(`Situação: ${filtersObj.situacao}`);
        if (filtersObj.regiaoName) filterParts.push(`Região: ${filtersObj.regiaoName}`);
        if (filtersObj.paisLabel) filterParts.push(`País: ${filtersObj.paisLabel}`);
        if (filtersObj.formaPagamentoName) filterParts.push(`Forma Pagamento: ${filtersObj.formaPagamentoName}`);
        if (filtersObj.hasCreditoAprovado) filterParts.push(`Crédito Aprovado: Sim`);
        if (filtersObj.minDesconto) filterParts.push(`Desc. Mín: ${filtersObj.minDesconto}%`);
        if (filtersObj.maxDesconto) filterParts.push(`Desc. Máx: ${filtersObj.maxDesconto}%`);
        if (filtersObj.dateFrom) filterParts.push(`De: ${filtersObj.dateFrom}`);
        if (filtersObj.dateTo) filterParts.push(`Até: ${filtersObj.dateTo}`);
        if (filtersObj.searchTerm) filterParts.push(`Busca: ${filtersObj.searchTerm}`);

        if (filterParts.length > 0) {
            worksheet.mergeCells(`A${filterRow}:N${filterRow}`);
            worksheet.getCell(`A${filterRow}`).value = `Filtros: ${filterParts.join(' | ')}`;
            worksheet.getCell(`A${filterRow}`).font = { size: 10, italic: true };
            filterRow++;
        }

        // Empty row
        filterRow++;

        // Headers
        const headers = [
            'Código', 'Razão Social', 'Nome Fantasia', 'CNPJ/CPF', 'I.E./VAT',
            'Tipo', 'Situação', 'Região', 'Forma Pagamento', 'Cond. Faturamento',
            'País', 'Desconto (%)', 'Crédito Aprovado', 'Data Cadastro'
        ];
        const headerRow = worksheet.addRow(headers);
        headerRow.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FF007bff' }
            };
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' }
            };
        });

        // Data rows
        items.forEach(client => {
            const row = worksheet.addRow([
                client.codigo || '-',
                client.razaoSocial || '-',
                client.nomeFantasia || '-',
                client.cnpjCpf || '-',
                client.inscricaoEstadual || '-',
                getTipoLabel(client.tipo),
                client.situacao || '-',
                getRegiaoName(client.regiaoId),
                getPaymentDocName(client.formaPagamentoId),
                getBillingConditionName(client.condicaoFaturamentoId),
                getCountryLabel(client.pais),
                client.percentualDesconto || '0',
                client.creditoAprovado ? 'Sim' : 'Não',
                client.createdAt ? new Date(client.createdAt).toLocaleDateString('pt-BR') : '-'
            ]);

            row.eachCell((cell, colNumber) => {
                cell.alignment = { vertical: 'middle' };
                if (colNumber !== 2 && colNumber !== 3) {
                    cell.alignment = { ...cell.alignment, horizontal: 'center' };
                }
                cell.border = {
                    top: { style: 'thin' },
                    left: { style: 'thin' },
                    bottom: { style: 'thin' },
                    right: { style: 'thin' }
                };
            });

            // Color status cell
            const statusCell = row.getCell(7);
            if (client.situacao === 'Ativo') {
                statusCell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FFd4edda' }
                };
            } else if (client.situacao === 'Inativo') {
                statusCell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FFF8D7DA' }
                };
            }

            // Color credit cell
            const creditCell = row.getCell(13);
            if (client.creditoAprovado) {
                creditCell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FFd4edda' }
                };
            }
        });

        // Summary row
        const summaryRow = worksheet.addRow([
            `Total de clientes: ${items.length}`,
            '', '', '', '', '', '', '', '', '', '', '', '', ''
        ]);
        summaryRow.getCell(1).font = { bold: true };
        worksheet.mergeCells(`A${worksheet.rowCount}:N${worksheet.rowCount}`);

        // Auto-size columns
        worksheet.columns.forEach(column => {
            let maxLength = 0;
            column.eachCell({ includeEmpty: true }, cell => {
                const cellValue = cell.value ? cell.value.toString() : '';
                maxLength = Math.max(maxLength, cellValue.length);
            });
            column.width = Math.min(maxLength + 2, 35);
        });

        // Generate file
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), fileName);
    };

    const generateReport = async () => {
        if (!currentUser) {
            addToast('Você precisa estar logado para gerar relatórios', 'error');
            return;
        }

        if (filteredClients.length === 0) {
            addToast('Nenhum cliente encontrado com os filtros selecionados.', 'warning');
            return;
        }

        setGenerating(true);
        addToast('Gerando relatório...', 'info');

        try {
            // Build filter display names
            const filterDisplay = {};

            if (filters.tipo) {
                filterDisplay.tipoLabel = getTipoLabel(filters.tipo);
            }
            if (filters.situacao) {
                filterDisplay.situacao = filters.situacao;
            }
            if (filters.regiaoId) {
                filterDisplay.regiaoName = getRegiaoName(filters.regiaoId);
            }
            if (filters.formaPagamentoId) {
                filterDisplay.formaPagamentoName = getPaymentDocName(filters.formaPagamentoId);
            }
            if (filters.condicaoFaturamentoId) {
                filterDisplay.condicaoFaturamentoName = getBillingConditionName(filters.condicaoFaturamentoId);
            }
            if (filters.pais) {
                filterDisplay.paisLabel = getCountryLabel(filters.pais);
            }
            if (filters.hasCreditoAprovado) {
                filterDisplay.hasCreditoAprovado = true;
            }
            if (filters.minDesconto) {
                filterDisplay.minDesconto = filters.minDesconto;
            }
            if (filters.maxDesconto) {
                filterDisplay.maxDesconto = filters.maxDesconto;
            }
            if (filters.dateFrom) {
                filterDisplay.dateFrom = new Date(filters.dateFrom).toLocaleDateString('pt-BR');
            }
            if (filters.dateTo) {
                filterDisplay.dateTo = new Date(filters.dateTo).toLocaleDateString('pt-BR');
            }
            if (filters.searchTerm) {
                filterDisplay.searchTerm = filters.searchTerm;
            }

            // Build report title
            let reportTitle = 'RELATÓRIO DE CLIENTES';
            if (filterDisplay.tipoLabel) reportTitle += ` - ${filterDisplay.tipoLabel}`;
            if (filterDisplay.situacao) reportTitle += ` - ${filterDisplay.situacao}`;
            if (filterDisplay.regiaoName) reportTitle += ` - ${filterDisplay.regiaoName}`;

            // Build filename
            const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
            const filterParts = [];
            if (filterDisplay.tipoLabel) filterParts.push(filterDisplay.tipoLabel.replace(/\s/g, '_'));
            if (filterDisplay.situacao) filterParts.push(filterDisplay.situacao.replace(/\s/g, '_'));
            if (filterDisplay.regiaoName) filterParts.push(filterDisplay.regiaoName.replace(/\s/g, '_'));
            const filterSuffix = filterParts.length > 0 ? `_${filterParts.join('_')}` : '';
            const fileName = `Relatorio_Clientes${filterSuffix}_${timestamp}.xlsx`;

            // Generate Excel file
            await generateExcelReport(
                filteredClients,
                filterDisplay,
                reportTitle,
                fileName
            );

            // Save to history
            const reportData = {
                fileName,
                generatedAt: serverTimestamp(),
                generatedBy: currentUser.email || currentUser.displayName || currentUser.uid,
                generatedByEmail: currentUser.email,
                generatedByName: currentUser.displayName || '',
                clientCount: filteredClients.length,
                filters: {
                    searchTerm: filters.searchTerm || null,
                    tipo: filters.tipo,
                    tipoLabel: filterDisplay.tipoLabel || null,
                    situacao: filters.situacao || null,
                    regiaoId: filters.regiaoId,
                    regiaoName: filterDisplay.regiaoName || null,
                    formaPagamentoId: filters.formaPagamentoId,
                    formaPagamentoName: filterDisplay.formaPagamentoName || null,
                    condicaoFaturamentoId: filters.condicaoFaturamentoId,
                    condicaoFaturamentoName: filterDisplay.condicaoFaturamentoName || null,
                    pais: filters.pais,
                    paisLabel: filterDisplay.paisLabel || null,
                    hasCreditoAprovado: filters.hasCreditoAprovado,
                    minDesconto: filters.minDesconto || null,
                    maxDesconto: filters.maxDesconto || null,
                    dateFrom: filters.dateFrom || null,
                    dateTo: filters.dateTo || null
                }
            };

            const docRef = await addDoc(collection(db, REPORT_HISTORY_COLLECTION), reportData);

            setReports([
                {
                    id: docRef.id,
                    ...reportData,
                    generatedAt: new Date()
                },
                ...reports
            ]);

            addToast(`Relatório "${fileName}" gerado com sucesso!`, 'success');
            setIsModalOpen(false);

        } catch (error) {
            console.error('Error generating report:', error);
            addToast(`Erro ao gerar relatório: ${error.message}`, 'error');
        } finally {
            setGenerating(false);
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setCurrentPage(1);
    };

    const handleResetFilters = () => {
        setFilters({
            searchTerm: '',
            tipo: '',
            situacao: '',
            regiaoId: '',
            formaPagamentoId: '',
            condicaoFaturamentoId: '',
            pais: '',
            hasCreditoAprovado: false,
            minDesconto: '',
            maxDesconto: '',
            dateFrom: '',
            dateTo: ''
        });
        setSearchTerm('');
        setCurrentPage(1);
    };

    const formatDate = (date) => {
        return date.toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getFilterDescription = (filtersObj) => {
        const parts = [];
        if (filtersObj.tipoLabel) parts.push(`Tipo: ${filtersObj.tipoLabel}`);
        if (filtersObj.situacao) parts.push(`Situação: ${filtersObj.situacao}`);
        if (filtersObj.regiaoName) parts.push(`Região: ${filtersObj.regiaoName}`);
        if (filtersObj.formaPagamentoName) parts.push(`Pagamento: ${filtersObj.formaPagamentoName}`);
        if (filtersObj.condicaoFaturamentoName) parts.push(`Faturamento: ${filtersObj.condicaoFaturamentoName}`);
        if (filtersObj.paisLabel) parts.push(`País: ${filtersObj.paisLabel}`);
        if (filtersObj.hasCreditoAprovado) parts.push(`Crédito Aprovado`);
        if (filtersObj.minDesconto) parts.push(`Desc. ≥ ${filtersObj.minDesconto}%`);
        if (filtersObj.maxDesconto) parts.push(`Desc. ≤ ${filtersObj.maxDesconto}%`);
        if (filtersObj.dateFrom) parts.push(`De: ${filtersObj.dateFrom}`);
        if (filtersObj.dateTo) parts.push(`Até: ${filtersObj.dateTo}`);
        if (filtersObj.searchTerm) parts.push(`Busca: ${filtersObj.searchTerm}`);
        if (parts.length === 0) return 'Todos os clientes';
        return parts.join(', ');
    };

    const openModal = () => {
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        handleResetFilters();
    };

    const isLoading = clientsLoading;

    // Check if any filter is active
    const hasActiveFilters = filters.tipo || filters.situacao || filters.regiaoId ||
        filters.formaPagamentoId || filters.condicaoFaturamentoId || filters.pais ||
        filters.hasCreditoAprovado || filters.minDesconto || filters.maxDesconto ||
        filters.dateFrom || filters.dateTo || filters.searchTerm;

    // Render filter form content for modal
    const renderFilterForm = () => (
        <>
            {paramsLoading && (
                <div className={styles.infoBox}>
                    Carregando parâmetros do sistema...
                </div>
            )}

            {/* Search Term */}
            <div className={styles.formGroup}>
                <label>Busca por Código, Razão Social, Fantasia ou Documento</label>
                <input
                    type="text"
                    placeholder="Digite para buscar..."
                    value={filters.searchTerm}
                    onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                    className={styles.input}
                />
            </div>

            {/* Basic Filters Row */}
            <div className={styles.formRow}>
                <div className={styles.formGroup}>
                    <label>Tipo de Cliente</label>
                    <select
                        value={filters.tipo}
                        onChange={(e) => handleFilterChange('tipo', e.target.value)}
                        className={styles.select}
                    >
                        <option value="">Todos os tipos</option>
                        {TIPO_CLIENTE_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>

                <div className={styles.formGroup}>
                    <label>Situação</label>
                    <select
                        value={filters.situacao}
                        onChange={(e) => handleFilterChange('situacao', e.target.value)}
                        className={styles.select}
                    >
                        <option value="">Todas</option>
                        {SITUACAO_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>

                <div className={styles.formGroup}>
                    <label>Região</label>
                    <select
                        value={filters.regiaoId}
                        onChange={(e) => handleFilterChange('regiaoId', e.target.value)}
                        className={styles.select}
                        disabled={regionsLoading}
                    >
                        <option value="">Todas as regiões</option>
                        {regions?.map(region => (
                            <option key={region.id} value={region.id}>{region.name}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Payment & Billing Filters */}
            <div className={styles.formRow}>
                <div className={styles.formGroup}>
                    <label>Forma de Pagamento</label>
                    <select
                        value={filters.formaPagamentoId}
                        onChange={(e) => handleFilterChange('formaPagamentoId', e.target.value)}
                        className={styles.select}
                        disabled={paymentLoading}
                    >
                        <option value="">Todas</option>
                        {paymentDocs?.map(payment => (
                            <option key={payment.id} value={payment.id}>{payment.nome}</option>
                        ))}
                    </select>
                </div>

                <div className={styles.formGroup}>
                    <label>Condição de Faturamento</label>
                    <select
                        value={filters.condicaoFaturamentoId}
                        onChange={(e) => handleFilterChange('condicaoFaturamentoId', e.target.value)}
                        className={styles.select}
                        disabled={billingLoading}
                    >
                        <option value="">Todas</option>
                        {billingConditions?.map(condition => (
                            <option key={condition.id} value={condition.id}>{condition.name}</option>
                        ))}
                    </select>
                </div>

                <div className={styles.formGroup}>
                    <label>País</label>
                    <select
                        value={filters.pais}
                        onChange={(e) => handleFilterChange('pais', e.target.value)}
                        className={styles.select}
                    >
                        <option value="">Todos os países</option>
                        {countryOptions.map(country => (
                            <option key={country.value} value={country.value}>{country.label}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Credit & Discount Filters */}
            <div className={styles.formRow}>
                <div className={styles.formGroup}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginTop: '28px' }}>
                        <input
                            type="checkbox"
                            checked={filters.hasCreditoAprovado}
                            onChange={(e) => handleFilterChange('hasCreditoAprovado', e.target.checked)}
                        />
                        Apenas clientes com crédito aprovado
                    </label>
                </div>

                <div className={styles.formGroup}>
                    <label>Desconto Mínimo (%)</label>
                    <input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="Ex: 5"
                        value={filters.minDesconto}
                        onChange={(e) => handleFilterChange('minDesconto', e.target.value)}
                        className={styles.input}
                    />
                </div>

                <div className={styles.formGroup}>
                    <label>Desconto Máximo (%)</label>
                    <input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="Ex: 20"
                        value={filters.maxDesconto}
                        onChange={(e) => handleFilterChange('maxDesconto', e.target.value)}
                        className={styles.input}
                    />
                </div>
            </div>

            {/* Date Range Filters */}
            <div className={styles.formRow}>
                <div className={styles.formGroup}>
                    <label>Data Cadastro (Início)</label>
                    <input
                        type="date"
                        value={filters.dateFrom}
                        onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                        className={styles.input}
                    />
                </div>
                <div className={styles.formGroup}>
                    <label>Data Cadastro (Fim)</label>
                    <input
                        type="date"
                        value={filters.dateTo}
                        onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                        className={styles.input}
                    />
                </div>
            </div>

            {/* Preview Info */}
            <div className={styles.fieldHint} style={{ textAlign: 'center', padding: '8px', background: 'var(--primary-light)', borderRadius: '6px' }}>
                📊 {filteredClients.length} {filteredClients.length === 1 ? 'cliente encontrado' : 'clientes encontrados'} com os filtros atuais
            </div>
        </>
    );

    return (
        <div className={styles.pageContainer}>
            {/* Toasts */}
            {toasts.length > 0 && (
                <div className={styles.toastContainer}>
                    {toasts.map(toast => (
                        <Toast
                            key={toast.id}
                            message={toast.message}
                            type={toast.type}
                            onClose={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
                        />
                    ))}
                </div>
            )}

            {/* Confirm Modal */}
            <ConfirmModal
                isOpen={isConfirmOpen}
                onClose={() => setIsConfirmOpen(false)}
                onConfirm={deleteReport}
                title="Excluir Relatório"
                message="Tem certeza que deseja excluir este relatório? Esta ação não pode ser desfeita."
                confirmText="Excluir"
                danger
            />

            {/* Filter Modal */}
            <FormModal
                isOpen={isModalOpen}
                onClose={closeModal}
                onSubmit={generateReport}
                title="Gerar Relatório de Clientes"
                subtitle="Selecione os filtros desejados para o relatório"
                isLoading={generating}
                submitText={`Gerar Relatório (${filteredClients.length} clientes)`}
                cancelText="Cancelar"
                maxWidth="860px"
            >
                {renderFilterForm()}
                <div className={styles.formActions} style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border-color)' }}>
                    <button type="button" onClick={handleResetFilters} className={styles.cancelButton}>
                        Limpar Filtros
                    </button>
                </div>
            </FormModal>

            {/* Main Header */}
            <PageHeader
                title="Listagem de Clientes"
                actionText="Gerar Relatório"
                onAction={openModal}
                disabled={!currentUser || isLoading}
            />

            {/* Active Filters Display */}
            {hasActiveFilters && (
                <div style={{ marginBottom: '16px', display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                    <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Filtros ativos:</span>
                    {filters.tipo && <span className={styles.filterBadge}>Tipo: {getTipoLabel(filters.tipo)}</span>}
                    {filters.situacao && <span className={styles.filterBadge}>Situação: {filters.situacao}</span>}
                    {filters.regiaoId && <span className={styles.filterBadge}>Região: {getRegiaoName(filters.regiaoId)}</span>}
                    {filters.formaPagamentoId && <span className={styles.filterBadge}>Pagamento: {getPaymentDocName(filters.formaPagamentoId)}</span>}
                    {filters.condicaoFaturamentoId && <span className={styles.filterBadge}>Faturamento: {getBillingConditionName(filters.condicaoFaturamentoId)}</span>}
                    {filters.pais && <span className={styles.filterBadge}>País: {getCountryLabel(filters.pais)}</span>}
                    {filters.hasCreditoAprovado && <span className={styles.filterBadge}>Crédito Aprovado</span>}
                    {filters.minDesconto && <span className={styles.filterBadge}>Desc. ≥ {filters.minDesconto}%</span>}
                    {filters.maxDesconto && <span className={styles.filterBadge}>Desc. ≤ {filters.maxDesconto}%</span>}
                    {filters.dateFrom && <span className={styles.filterBadge}>De: {new Date(filters.dateFrom).toLocaleDateString('pt-BR')}</span>}
                    {filters.dateTo && <span className={styles.filterBadge}>Até: {new Date(filters.dateTo).toLocaleDateString('pt-BR')}</span>}
                </div>
            )}

            

            {/* Report History Section */}
            <div className={styles.tableContainer} style={{ marginTop: '32px' }}>
                <h3 className={styles.tableTitle} style={{ padding: '16px 20px', margin: 0, borderBottom: '1px solid var(--border-color)' }}>
                    Histórico de Relatórios Gerados
                </h3>
                <div style={{ overflowX: 'auto' }}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th style={{ width: '160px' }}>Data/Hora</th>
                                <th>Nome do Arquivo</th>
                                <th style={{ width: '200px' }}>Gerado por</th>
                                <th style={{ width: '80px' }}>Clientes</th>
                                <th style={{ width: '250px' }}>Filtros Aplicados</th>
                                <th style={{ width: '80px' }}>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loadingReports ? (
                                <tr>
                                    <td colSpan={6} className={styles.emptyTable}>
                                        <div className={styles.spinner} style={{ margin: '0 auto' }}></div>
                                        <span style={{ marginTop: '8px', display: 'block' }}>Carregando histórico...</span>
                                    </td>
                                </tr>
                            ) : reports.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className={styles.emptyTable}>
                                        Nenhum relatório gerado ainda. Clique em "Gerar Relatório" para começar.
                                    </td>
                                </tr>
                            ) : (
                                reports.map((report) => (
                                    <tr key={report.id}>
                                        <td>{formatDate(report.generatedAt)}</td>
                                        <td>
                                            <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                                                {report.fileName}
                                            </span>
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                <span style={{ fontWeight: 500 }}>
                                                    {report.generatedByName || report.generatedBy?.split('@')[0] || 'Usuário'}
                                                </span>
                                                {report.generatedByEmail && (
                                                    <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                                                        {report.generatedByEmail}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>{report.clientCount}</td>
                                        <td style={{ fontSize: '12px' }}>
                                            {getFilterDescription(report.filters)}
                                        </td>
                                        <td>
                                            <button
                                                onClick={() => handleDeleteClick(report.id)}
                                                className={`${styles.actionButton} ${styles.deleteButton}`}
                                                style={{ padding: '4px 12px', fontSize: '12px' }}
                                            >
                                                Excluir
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                    
                </div>
                {/* Pagination */}
                {totalPages > 1 && (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalItems={filteredClients?.length || 0}
                        onPageChange={setCurrentPage}
                        itemsLabel="clientes"
                    />
                )}
            </div>
        </div>
    );
};

export default ClientListReportPage;